import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save } from 'lucide-react';

interface ProviderLimits {
  requestsPerDay?: number;
  requestsPerFiveHours?: number;
  sizeBasedMultiplier?: boolean;
  minTokens?: number;
  maxTokens?: number;
  minMultiplier?: number;
  maxMultiplier?: number;
  unlimited?: boolean;
}

interface ProviderConfig {
  id: string;
  name: string;
  enabled: boolean;
  model?: string;
  apiKeyEnvVar?: string;
  baseUrl?: string;
  limits?: ProviderLimits;
}

interface ProviderLimitsFormProps {
  provider: ProviderConfig;
  onSave: (provider: ProviderConfig) => void;
  onCancel: () => void;
}

export function ProviderLimitsForm({ provider, onSave, onCancel }: ProviderLimitsFormProps) {
  const [formState, setFormState] = useState<ProviderConfig>(provider);
  const [isDirty, setIsDirty] = useState(false);

  const handleChange = (field: string, value: any) => {
    const keys = field.split('.');
    const newState = JSON.parse(JSON.stringify(formState));

    let current: any = newState;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }

    current[keys[keys.length - 1]] = value;
    setFormState(newState);
    setIsDirty(true);
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle>Configure Provider Limits</CardTitle>
        <CardDescription>
          Set usage limits and token multipliers for {provider.name}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Provider Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Provider Name</Label>
            <Input
              value={formState.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., My Local LLM"
            />
          </div>
          <div className="space-y-2">
            <Label>Model</Label>
            <Input
              value={formState.model || ''}
              onChange={(e) => handleChange('model', e.target.value)}
              placeholder="e.g., llama3:latest"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Base URL</Label>
            <Input
              value={formState.baseUrl || ''}
              onChange={(e) => handleChange('baseUrl', e.target.value)}
              placeholder="http://localhost:11434/v1"
            />
          </div>
          <div className="space-y-2">
            <Label>API Key Environment Variable</Label>
            <Input
              value={formState.apiKeyEnvVar || ''}
              onChange={(e) => handleChange('apiKeyEnvVar', e.target.value)}
              placeholder="MY_LLM_API_KEY"
            />
          </div>
        </div>

        <Separator />

        {/* Limits Configuration */}
        <div className="flex items-center space-x-2">
          <Switch
            id="unlimited"
            checked={formState.limits?.unlimited || false}
            onCheckedChange={(checked) => handleChange('limits.unlimited', checked)}
          />
          <Label htmlFor="unlimited">Unlimited Usage</Label>
        </div>

        {!formState.limits?.unlimited && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Requests per Day</Label>
                <Input
                  type="number"
                  value={formState.limits?.requestsPerDay || ''}
                  onChange={(e) => handleChange('limits.requestsPerDay', parseInt(e.target.value) || undefined)}
                  placeholder="e.g., 1000"
                />
              </div>
              <div className="space-y-2">
                <Label>Requests per 5 Hours</Label>
                <Input
                  type="number"
                  value={formState.limits?.requestsPerFiveHours || ''}
                  onChange={(e) => handleChange('limits.requestsPerFiveHours', parseInt(e.target.value) || undefined)}
                  placeholder="e.g., 200"
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="size-based"
                  checked={formState.limits?.sizeBasedMultiplier || false}
                  onCheckedChange={(checked) => handleChange('limits.sizeBasedMultiplier', checked)}
                />
                <Label htmlFor="size-based">Enable Size-based Token Multiplier</Label>
              </div>

              {formState.limits?.sizeBasedMultiplier && (
                <div className="space-y-4 pl-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Min Tokens ({formState.limits?.minTokens || 16000})</Label>
                      <Slider
                        value={[formState.limits?.minTokens || 16000]}
                        onValueChange={([value]) => handleChange('limits.minTokens', value)}
                        min={1000}
                        max={32000}
                        step={1000}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Tokens ({formState.limits?.maxTokens || 128000})</Label>
                      <Slider
                        value={[formState.limits?.maxTokens || 128000]}
                        onValueChange={([value]) => handleChange('limits.maxTokens', value)}
                        min={32000}
                        max={200000}
                        step={1000}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Min Multiplier ({formState.limits?.minMultiplier?.toFixed(2) || '1.00'})</Label>
                      <Slider
                        value={[formState.limits?.minMultiplier || 1.0]}
                        onValueChange={([value]) => handleChange('limits.minMultiplier', value)}
                        min={0.1}
                        max={2.0}
                        step={0.1}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Multiplier ({formState.limits?.maxMultiplier?.toFixed(2) || '5.00'})</Label>
                      <Slider
                        value={[formState.limits?.maxMultiplier || 5.0]}
                        onValueChange={([value]) => handleChange('limits.maxMultiplier', value)}
                        min={1.0}
                        max={10.0}
                        step={0.1}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={() => onSave(formState)} disabled={!isDirty}>
            <Save className="mr-2 h-4 w-4" />
            Save Provider
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}